import { falAngleRight } from 'shared/@generated/icons/falAngleRight';
import { falAward } from 'shared/@generated/icons/falAward';
import { falComment } from 'shared/@generated/icons/falComment';
import { falFingerprint } from 'shared/@generated/icons/falFingerprint';
import { falShieldKeyhole } from 'shared/@generated/icons/falShieldKeyhole';
import { fasPartyHorn } from 'shared/@generated/icons/fasPartyHorn';
import { useRoute } from 'shared/library/routes/route';
import { Platform, View } from 'react-native';
import { CountryCode, Listing, MarketplaceProduct, Paginator, Props } from 'shared/@types';
import { Trans, useTranslation } from 'react-i18next';
import { PageBuilder } from 'shared/library/page-builder';
import { H1, H2 } from 'shared/components/title';
import { Text } from 'shared/components/text';
import { Button } from 'shared/components/button';
import { Icon } from 'shared/components/icon';
import { staticImage } from 'shared/library/static-images';
import { Image } from 'shared/components/image';
import { ListingCard } from 'shared/components/listing-card';
import { BreederSocialProof } from 'shared/components/breeder-social-proof';
import { ReviewsSummaryCard } from 'shared/components/reviews-summary-card';
import { AdBanner } from 'shared/components/ad-banner';
import { AdAnimalProvider } from 'shared/components/ad-banner/ad-animal-context';
import { PublishListing } from 'shared/components/illustrations/publish-listing';
import { Inbox } from 'shared/components/illustrations/inbox';
import { Payment } from 'shared/components/illustrations/payment';
import { Pickup } from 'shared/components/illustrations/pickup';
import { BreederProfile } from 'shared/components/illustrations/breeder-profile';
import { ManagePets } from 'shared/components/illustrations/manage-pets';
import { Inbox as BreederInbox } from 'shared/components/illustrations/inbox';
import { PurchaseAgreement } from 'shared/components/illustrations/purchase-agreement';
import { withNativeGradientFallback } from 'shared/library/native-gradient';
import { HowItWorksSection } from 'shared/components/how-it-works-section';
import { ReviewsSegment } from 'shared/components/reviews-segment';
import { SellerBreederToolsSection } from 'shared/components/seller-breeder-tools-section';
import { getCountryName } from 'shared/library/i18n/country-name';
import { useFormatter } from 'shared/library/use-formatter';
import { loadSellingADogPageData } from 'shared/library/selling-a-dog-page-data';
import { Link } from 'shared/components/link';
import { translateArray } from 'shared/library/i18n/get-translation-list';
import { clsx } from 'clsx';

interface SellingADogScreenProps extends Props {
  listings: Paginator<Listing>;
  listingCountry: CountryCode;
  listingProduct: MarketplaceProduct | null;
}

const SellingADogScreen = (props: SellingADogScreenProps) => (
  <AdAnimalProvider animal={'dog'}>
    <SellingADogContent {...props} />
  </AdAnimalProvider>
);

const SellingADogContent = ({
  listings,
  listingCountry,
  listingProduct,
}: SellingADogScreenProps) => {
  const { t } = useTranslation('screens');
  const { formatCurrency } = useFormatter();
  const listingCountryName = getCountryName(t, listingCountry) ?? listingCountry;
  const route = useRoute();
  const isNative = Platform.OS !== 'web';
  const exampleListings = listings.data.filter(
    (listing) => listing.media_assets?.[0]?.url || listing.avatar?.url,
  );
  const faqItems = translateArray<{ question: string; answer: string }>(
    t,
    'selling_a_dog.faq.items',
    {
      country: listingCountryName,
    },
  );

  const howItWorksItems = [
    {
      title: t('selling_a_dog.how_it_works_items.create_listing.title'),
      description: t('selling_a_dog.how_it_works_items.create_listing.description'),
      image: <PublishListing />,
    },
    {
      title: t('selling_a_dog.how_it_works_items.connect_with_buyers.title'),
      description: t('selling_a_dog.how_it_works_items.connect_with_buyers.description'),
      image: <Inbox />,
    },
    {
      title: t('selling_a_dog.how_it_works_items.payment_and_agreement.title'),
      description: t('selling_a_dog.how_it_works_items.payment_and_agreement.description'),
      image: <Payment />,
    },
    {
      title: t('selling_a_dog.how_it_works_items.handover.title'),
      description: t('selling_a_dog.how_it_works_items.handover.description'),
      image: <Pickup />,
    },
  ];

  const supportItems = [
    {
      title: t('selling_a_dog.support_items.verified_buyers.title'),
      text: t('selling_a_dog.support_items.verified_buyers.text'),
      icon: falFingerprint,
    },
    {
      title: t('selling_a_dog.support_items.petpay.title'),
      text: t('selling_a_dog.support_items.petpay.text'),
      icon: falShieldKeyhole,
    },
    {
      title: t('selling_a_dog.support_items.breeder_profile.title'),
      text: t('selling_a_dog.support_items.breeder_profile.text'),
      icon: falAward,
    },
  ];
  const breederToolsItems = [
    {
      title: t('breeder_tools.tools.breeder_profile'),
      text: t('breeder_tools.tools.breeder_profile_description'),
      image: <BreederProfile />,
    },
    {
      title: t('breeder_tools.tools.pet_management'),
      text: t('breeder_tools.tools.pet_management_description'),
      image: <ManagePets />,
    },
    {
      title: t('breeder_tools.tools.documents_certificates'),
      text: t('breeder_tools.tools.documents_certificates_description'),
      image: <PurchaseAgreement />,
    },
    {
      title: t('breeder_tools.tools.buyer_communication'),
      text: t('breeder_tools.tools.buyer_communication_description'),
      image: <BreederInbox />,
    },
  ];

  return (
    <View>
      <View
        className={withNativeGradientFallback(
          clsx(
            'from-brand-cream-light via-brand-cream-light to-brand-cream bg-gradient-to-b',
            !isNative && 'relative left-1/2 right-1/2 -mx-[50vw] w-screen',
          ),
        )}
      >
        <View className={'px-3 py-6 md:px-6 md:py-12'}>
          <View className={'mx-auto w-full max-w-screen-xl'}>
            <View className={'gap-3 md:flex-row md:items-center md:gap-6 lg:gap-20'}>
              <View className={'md:w-1/2'}>
                <Image
                  src={staticImage('mom-with-puppies')}
                  alt={t('selling_a_dog.image_alt')}
                  containerClassName={'aspect-4/3 w-full'}
                  imageClassName={'rounded-lg object-cover'}
                  priority={true}
                  sizeType={'large'}
                />

                <View className={'absolute bottom-0 right-0 p-3'}>
                  <ReviewsSummaryCard className={'bg-white'} />
                </View>
              </View>

              <View className={'flex-1'}>
                <View
                  className={
                    'mb-3 flex-row items-center gap-1.5 self-start rounded-full border border-yellow-950/15 bg-yellow-400 px-3 py-1.5 md:mb-6'
                  }
                >
                  <Icon icon={fasPartyHorn} className={'mr-1 size-3 text-yellow-950 md:size-4'} />

                  <Text className={'text-xs font-semibold text-yellow-950 md:text-sm'}>
                    {t('selling_a_dog.sold_over_100k')}
                  </Text>
                </View>

                <H1
                  title={t('selling_a_dog.title')}
                  hero={true}
                  className={'max-w-xl'}
                  marginBottom={false}
                />

                <Text
                  className={
                    'text-brand-brown-dark/80 mt-5 max-w-xl text-lg leading-relaxed md:text-xl'
                  }
                >
                  {t('selling_a_dog.description')}
                </Text>

                <View className={'mt-3 gap-1.5 md:mt-6 md:flex-row md:gap-3'}>
                  <Button
                    text={t('selling_a_dog.publish_listing')}
                    icon={falAngleRight}
                    color={'brown'}
                    href={route('listings.create')}
                  />
                  <Button
                    text={t('selling_a_dog.create_breeder_profile')}
                    icon={falAngleRight}
                    color={'white'}
                    href={route('me.breeder.index')}
                  />
                </View>

                <BreederSocialProof
                  prefix={t('selling_a_dog.social_proof.prefix')}
                  count={t('selling_a_dog.social_proof.count')}
                  suffix={t('selling_a_dog.social_proof.suffix')}
                />
              </View>
            </View>

            <View
              className={
                isNative ? 'mt-12 gap-8' : 'mt-12 grid gap-8 sm:grid-cols-3 sm:gap-12 md:mt-20'
              }
            >
              {supportItems.map((item) => (
                <View key={item.title} className={'flex-row items-start gap-4 sm:pr-6'}>
                  <View
                    className={
                      'bg-brand-cream-dark flex size-10 shrink-0 items-center justify-center rounded-md'
                    }
                  >
                    <Icon icon={item.icon} className={'text-brand-brown-dark size-4'} />
                  </View>

                  <View className={'flex-1 gap-3'}>
                    <Text className={'text-brand-brown-dark text-lg font-semibold leading-6'}>
                      {item.title}
                    </Text>

                    <Text className={'text-brand-brown-dark/72 max-w-sm text-base leading-7'}>
                      {item.text}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>

      <View className={'p-3 md:p-6 md:py-20'}>
        <View className={'mx-auto w-full max-w-screen-xl'}>
          {!isNative && <AdBanner className={'mb-12 md:mb-20'} placement={'top'} />}

          <HowItWorksSection
            id={'how-it-works'}
            title={t('selling_a_dog.how_it_works')}
            subtitle={t('selling_a_dog.how_it_works_description')}
            items={howItWorksItems}
            marginTop={false}
          />

          <View className={'mt-8 flex items-start md:mt-10 md:items-center'}>
            <Button
              text={t('selling_a_dog.publish_your_listing')}
              icon={falAngleRight}
              color={'brown'}
              href={route('listings.create')}
            />
          </View>

          <View className={isNative ? 'mt-12' : 'mt-20 md:mt-28'}>
            <H2
              title={t('selling_a_dog.rehoming.title')}
              subtitle={t('selling_a_dog.rehoming.text')}
              marginTop={false}
            />
            <Text className={'text-brand-brown-dark/75 md:text-lg'}>
              <Trans
                t={t}
                i18nKey={'selling_a_dog.rehoming.guide'}
                components={{
                  guide: (
                    <Link
                      href={route('blog.show', {
                        post: {
                          en: 'how-to-choose-a-buyer',
                          sv: 'sa-valjer-du-kopare',
                          da: 'sa-vaelger-du-koebere',
                          no: 'slik-velger-du-kjoper',
                        },
                      })}
                      asInline
                      containerClassName={'underline'}
                      textClassName={'underline'}
                    />
                  ),
                }}
              />
            </Text>
          </View>

          {exampleListings.length > 0 && (
            <View className={isNative ? 'mt-12' : 'mt-20 md:mt-28'}>
              <View className={'mx-auto max-w-screen-lg'}>
                <H2
                  title={t('selling_a_dog.examples.title')}
                  subtitle={t('selling_a_dog.examples.text', { country: listingCountryName })}
                  center={true}
                  marginTop={false}
                />
              </View>

              <View
                className={
                  isNative
                    ? 'mt-6 gap-6'
                    : 'flex-cols-1 md:flex-cols-2 lg:flex-cols-4 mt-6 flex-row flex-wrap gap-6'
                }
              >
                {exampleListings.slice(0, 4).map((listing) => (
                  <View key={listing.id}>
                    <ListingCard listing={listing} />
                  </View>
                ))}
              </View>

              <View className={'mt-8 items-start md:mt-10 md:items-center'}>
                <Button
                  text={t('selling_a_dog.examples.cta')}
                  icon={falAngleRight}
                  color={'brown'}
                  href={route('listings.dogs', undefined, {
                    query: { country: listingCountry },
                  })}
                />
              </View>
            </View>
          )}

          <SellerBreederToolsSection
            title={t('selling_a_dog.breeder_tools_section.title')}
            subtitle={t('selling_a_dog.breeder_tools_section.text')}
            items={breederToolsItems}
            ctaText={t('selling_a_dog.breeder_tools_section.cta')}
            ctaHref={route('breeder-tools')}
          />

          <View className={isNative ? 'mt-12' : 'mt-20 md:mt-28'}>
            {!isNative && <AdBanner className={'mb-12 md:mb-20'} placement={'content'} />}

            <H2
              title={t('selling_a_dog.faq.title')}
              subtitle={t('selling_a_dog.faq.text')}
              center={true}
              marginTop={false}
            />

            <View className={isNative ? 'mt-6 gap-6' : 'mt-6 grid gap-6 md:grid-cols-2'}>
              <View className={'pb-2'}>
                <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                  {t('selling_a_dog.pricing.question')}
                </Text>
                <Text className={'text-brand-brown-dark/75 mt-2'}>
                  {listingProduct
                    ? t('selling_a_dog.pricing.answer', {
                        price: formatCurrency(
                          listingProduct.localized_price.amount_minor / 100,
                          listingProduct.localized_price.currency,
                          2,
                        ),
                        days: listingProduct.duration.d,
                      })
                    : t('selling_a_dog.pricing.unavailable')}
                </Text>
              </View>
              {faqItems.map((item) => (
                <View key={item.question} className={'pb-2'}>
                  <Text className={'text-brand-brown-dark font-bold md:text-lg'}>
                    {item.question}
                  </Text>
                  <Text className={'text-brand-brown-dark/75 mt-2'}>{item.answer}</Text>
                </View>
              ))}
            </View>

            <View
              className={
                'mt-8 flex-col items-start gap-3 md:flex-row md:items-center md:justify-center'
              }
            >
              <Text className={'text-brand-brown-dark/75'}>{t('selling_a_dog.faq.help_text')}</Text>

              <Button
                text={t('selling_a_dog.faq.cta')}
                icon={falComment}
                iconPosition={'left'}
                color={'white'}
                href={route('help.index')}
              />
            </View>
          </View>

          <View className={isNative ? 'mt-12' : 'mt-20 md:mt-28'}>
            <ReviewsSegment role={'seller'} />
          </View>
        </View>
      </View>
    </View>
  );
};

SellingADogScreen.pageBuilder = new PageBuilder<SellingADogScreenProps>().request(({ req, ctx }) =>
  loadSellingADogPageData(req, ctx),
);

export { SellingADogScreen };
export type { SellingADogScreenProps };
